import { ChartCreatorValidationError } from "../../../shared/factset/chartCreatorPayload";

/** A chart frame on a slide, in points. */
export interface ChartFrame {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ChartSlideBounds {
  slideWidth: number;
  slideHeight: number;
}

/** How one chart route names a malformed target. */
export interface ChartTargetRules {
  /** Validation error code. */
  invalidCode: string;
  /** Route name that starts each message, such as "ActiveGraph". */
  label: string;
  /** Target fields accepted besides left, top, width and height. */
  otherFields: readonly string[];
}

const FRAME_FIELDS = ["left", "top", "width", "height"] as const;

/** Gap kept between a chart and the slide edge. */
const SAFE_MARGIN = 20;

/** Smallest width or height a clamped frame keeps. */
const MIN_SIZE = 20;

export function hasFrameFields(target: Partial<ChartFrame> | undefined): boolean {
  return FRAME_FIELDS.some((field) => target?.[field] !== undefined);
}

export function assertValidChartTarget(target: unknown, rules: ChartTargetRules): void {
  if (
    target !== undefined &&
    (!target || typeof target !== "object" || Array.isArray(target))
  ) {
    throw new ChartCreatorValidationError(rules.invalidCode, `${rules.label} target must be an object.`);
  }
  const fields = (target ?? {}) as Record<string, unknown>;
  const allowedFields = new Set<string>([...rules.otherFields, ...FRAME_FIELDS]);
  const unsupportedField = Object.keys(fields).find((field) => !allowedFields.has(field));
  if (unsupportedField) {
    throw new ChartCreatorValidationError(
      rules.invalidCode,
      `${rules.label} target does not accept "${unsupportedField}".`,
    );
  }
  for (const field of FRAME_FIELDS) {
    const value = fields[field];
    if (value === undefined) continue;
    if (typeof value !== "number" || !Number.isFinite(value)) {
      throw new ChartCreatorValidationError(
        rules.invalidCode,
        `${rules.label} frame ${field} must be a finite number.`,
      );
    }
    if (field === "left" || field === "top" ? value < 0 : value <= 0) {
      throw new ChartCreatorValidationError(
        rules.invalidCode,
        `${rules.label} frame ${field} is outside the supported range.`,
      );
    }
  }
}

/** The host's slide size when it is usable, otherwise 960 x 540. */
export function usableSlideBounds(bounds: ChartSlideBounds | null | undefined): ChartSlideBounds {
  const usable = (value: number | undefined, fallback: number) =>
    value !== undefined && Number.isFinite(value) && value >= SAFE_MARGIN * 3 ? value : fallback;
  return {
    slideWidth: usable(bounds?.slideWidth, 960),
    slideHeight: usable(bounds?.slideHeight, 540),
  };
}

/**
 * Frame for a new chart: requested values over `defaults`, with the position
 * kept inside the slide margin first and the size then cut to what fits there.
 */
export function clampNewChartFrame(
  request: Partial<ChartFrame> | undefined,
  bounds: ChartSlideBounds | null | undefined,
  defaults: ChartFrame,
): ChartFrame {
  const { slideWidth, slideHeight } = usableSlideBounds(bounds);
  const left = Math.max(
    SAFE_MARGIN,
    Math.min(request?.left ?? defaults.left, slideWidth - SAFE_MARGIN - MIN_SIZE),
  );
  const top = Math.max(
    SAFE_MARGIN,
    Math.min(request?.top ?? defaults.top, slideHeight - SAFE_MARGIN - MIN_SIZE),
  );
  const width = Math.max(
    MIN_SIZE,
    Math.min(request?.width ?? defaults.width, slideWidth - left - SAFE_MARGIN),
  );
  const height = Math.max(
    MIN_SIZE,
    Math.min(request?.height ?? defaults.height, slideHeight - top - SAFE_MARGIN),
  );
  return { left, top, width, height };
}

/**
 * Frame for an existing chart: requested values over its current frame, with
 * the size kept inside the slide margin first and the position then moved so
 * the whole frame fits. Undefined when no frame value was requested.
 */
export function clampChartFrameUpdate(
  request: Partial<ChartFrame> | undefined,
  current: Partial<ChartFrame>,
  bounds: ChartSlideBounds | null | undefined,
  unavailable: { code: string; message: string },
): ChartFrame | undefined {
  if (!request || !hasFrameFields(request)) {
    return undefined;
  }
  const { slideWidth, slideHeight } = usableSlideBounds(bounds);
  const desiredLeft = request.left ?? current.left;
  const desiredTop = request.top ?? current.top;
  const desiredWidth = request.width ?? current.width;
  const desiredHeight = request.height ?? current.height;
  if (
    desiredLeft === undefined ||
    desiredTop === undefined ||
    desiredWidth === undefined ||
    desiredHeight === undefined
  ) {
    throw new ChartCreatorValidationError(unavailable.code, unavailable.message);
  }
  const width = Math.max(MIN_SIZE, Math.min(desiredWidth, slideWidth - SAFE_MARGIN * 2));
  const height = Math.max(MIN_SIZE, Math.min(desiredHeight, slideHeight - SAFE_MARGIN * 2));
  const left = Math.max(SAFE_MARGIN, Math.min(desiredLeft, slideWidth - SAFE_MARGIN - width));
  const top = Math.max(SAFE_MARGIN, Math.min(desiredTop, slideHeight - SAFE_MARGIN - height));
  return { left, top, width, height };
}
